import { z } from 'zod';

export const OrigemGravacaoSchema = z.enum(['UPLOAD', 'MICROFONE', 'IMPORTADA']);
export type OrigemGravacao = z.infer<typeof OrigemGravacaoSchema>;

export const EstadoGravacaoSchema = z.enum([
  'AGUARDANDO_ENVIO',
  'GRAVANDO',
  'PROCESSANDO',
  'TRANSCREVENDO',
  'CONCLUIDA',
  'ERRO',
]);
export type EstadoGravacao = z.infer<typeof EstadoGravacaoSchema>;

/** Modelos de transcrição da Groq (preço por hora de áudio em USD). */
export const MODELOS_STT = [
  { id: 'whisper-large-v3-turbo', rotulo: 'Whisper large v3 turbo', usdPorHora: 0.04, padrao: true },
  { id: 'whisper-large-v3', rotulo: 'Whisper large v3', usdPorHora: 0.111, padrao: false },
] as const;
export type IdModeloStt = (typeof MODELOS_STT)[number]['id'];
export const ModeloSttSchema = z.enum(
  MODELOS_STT.map((m) => m.id) as [IdModeloStt, ...IdModeloStt[]],
);
export const MODELO_STT_PADRAO: IdModeloStt = 'whisper-large-v3-turbo';

export const MIMES_AUDIO = [
  'audio/flac',
  'audio/mpeg',
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a',
  'audio/ogg',
  'audio/opus',
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'video/mp4',
  'video/webm',
] as const;

export const CriarGravacaoSchema = z.object({
  titulo: z.string().min(1).max(200),
  mime: z.enum(MIMES_AUDIO),
  tamanho: z.number().int().positive(),
  modelo: ModeloSttSchema.default(MODELO_STT_PADRAO),
  vocabulario: z.string().max(2000).optional(),
  sigilosa: z.boolean().default(false),
});
export type CriarGravacao = z.infer<typeof CriarGravacaoSchema>;

export const ConfirmarUploadSchema = z.object({
  etag: z.string().min(1),
  tamanho: z.number().int().positive(),
});
export type ConfirmarUpload = z.infer<typeof ConfirmarUploadSchema>;

export const ImportarGravacaoSchema = z.object({
  maquinaId: z.string().min(1),
  caminho: z.string().min(1).max(1000),
  titulo: z.string().min(1).max(200).optional(),
  modelo: ModeloSttSchema.default(MODELO_STT_PADRAO),
  sigilosa: z.boolean().default(false),
});
export type ImportarGravacao = z.infer<typeof ImportarGravacaoSchema>;

export const AtualizarGravacaoSchema = z
  .object({
    titulo: z.string().min(1).max(200).optional(),
    compartilhada: z.boolean().optional(),
    vocabulario: z.string().max(2000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'informe ao menos um campo');
export type AtualizarGravacao = z.infer<typeof AtualizarGravacaoSchema>;

/** Gravacao pelo microfone: nasce sem arquivo, e o audio chega em pedacos. */
export const IniciarGravacaoSchema = z.object({
  titulo: z.string().min(1).max(200),
  modelo: ModeloSttSchema.default(MODELO_STT_PADRAO),
  vocabulario: z.string().max(2000).optional(),
  sigilosa: z.boolean().default(false),
});
export type IniciarGravacao = z.infer<typeof IniciarGravacaoSchema>;

/**
 * O navegador avisa que o trecho chegou ao destino. Os tempos vem de la
 * porque so quem gravou sabe onde aquele pedaco cai na linha do tempo.
 */
export const ConfirmarTrechoSchema = z.object({
  ordem: z.number().int().nonnegative(),
  inicioMs: z.number().int().nonnegative(),
  fimMs: z.number().int().positive(),
  tamanho: z.number().int().positive(),
});
export type ConfirmarTrecho = z.infer<typeof ConfirmarTrechoSchema>;

export const ConfirmarParteSchema = z.object({
  ordem: z.number().int().nonnegative(),
  tamanho: z.number().int().positive(),
});
export type ConfirmarParte = z.infer<typeof ConfirmarParteSchema>;

export const UrlsTrechosSchema = z.object({
  ordens: z.array(z.number().int().nonnegative()).min(1).max(50),
});
export type UrlsTrechos = z.infer<typeof UrlsTrechosSchema>;

export const BuscarGravacoesSchema = z.object({
  q: z.string().min(2).max(200).optional(),
});
export type BuscarGravacoes = z.infer<typeof BuscarGravacoesSchema>;

/**
 * A Groq cobra no mínimo 10 s por requisição e o Whisper foi treinado em
 * janelas de 30 s: trechos curtos custam caro e alucinam mais.
 */
export const TRECHO_MINIMO_MS = 10_000;
export const TRECHO_ALVO_MS = 25_000;
export const TRECHO_MAXIMO_MS = 28_000;
export const SOBREPOSICAO_MS = 1_500;
export const VALIDADE_URL_ASSINADA_S = 600;

/**
 * Teto do `PUT` de um trecho de áudio ao vivo (F1.3 da revisão de out/2026).
 * Ninguém declara o tamanho de um trecho, então vale um teto da espécie: WAV
 * de 16 kHz mono em 16 bits é 32 KB/s, e o trecho mais longo
 * (`TRECHO_MAXIMO_MS` + `SOBREPOSICAO_MS`) dá ~0,95 MB. 4 MiB são ~2 min de
 * folga — sobra para mudar o corte, não sobra para encher o disco.
 */
export const TETO_TRECHO_BYTES = 4 * 1024 * 1024;

/**
 * Teto do `PUT` de uma parte do áudio original (o `MediaRecorder`, a cada
 * 30 s). Opus costuma dar ~16 KB/s; 8 MiB aceitam até ~2 Mbit/s, que é o que
 * um navegador mandaria no pior caso de taxa.
 */
export const TETO_PARTE_BYTES = 8 * 1024 * 1024;
